import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
} from "@tanstack/react-query"
import { AxiosError } from "axios"
import i18next from "i18next"
import { useTranslation } from "react-i18next"

import { CountryIso3166, LibraryResponses } from "@tfg/types/endpoints/library"
import {
  productionSitesQueryValidationType,
  riskQueryValidationType,
  SupplierGetQueryValidationType,
  supplierMonitoringFiltersValidationType,
  transferContactValidationType,
} from "@tfg/types/schemas/organization/schemas"
import { fetchDataOptionsParam } from "@tfg/types/schemas/products"

import { httpClient } from "~/backend/axios.ts"
import { queryClient } from "~/backend/queryClient.ts"
import { showAxiosError } from "~/utils/showAxiosError"

export const useListSuppliersQuery = ({
  fetchDataOptions,
  searchSupplier,
  filters,
  orderBy,
}: Omit<SupplierGetQueryValidationType, "filters"> &
  fetchDataOptionsParam & {
    filters: supplierMonitoringFiltersValidationType
  }) => {
  return useQuery<LibraryResponses.SuppliersResponse>({
    queryKey: ["suppliers", fetchDataOptions, searchSupplier, filters, orderBy],

    queryFn: async () => {
      const skip = fetchDataOptions.pageIndex * fetchDataOptions.pageSize
      const take = fetchDataOptions.pageSize
      const res = await httpClient.get<LibraryResponses.SuppliersResponse>(
        "suppliers/monitoring",
        {
          params: {
            skip,
            take,
            searchSupplier,
            filters: JSON.stringify(filters),
            orderBy,
          },
        }
      )
      return res.data
    },

    placeholderData: keepPreviousData,
  })
}

export const useListSuppliersCountriesQuery = () => {
  return useQuery<CountryIso3166[]>({
    queryKey: ["suppliersCountries"],

    queryFn: async () => {
      const res =
        await httpClient.get<LibraryResponses.SuppliersCountriesResponse>(
          "traceability/suppliers/countries"
        )
      return res.data?.countries || []
    },
  })
}

export const useListSuppliersProductionSitesQuery = ({
  fetchDataOptions,
  searchProductionSite,
}: productionSitesQueryValidationType & fetchDataOptionsParam) => {
  return useQuery<LibraryResponses.SupplierProductionSitesResponse>({
    queryKey: ["suppliers-prodsites", fetchDataOptions, searchProductionSite],

    queryFn: async () => {
      const res =
        await httpClient.get<LibraryResponses.SupplierProductionSitesResponse>(
          "suppliers/production-sites",
          {
            headers: {
              "Accept-Language": i18next.language.toUpperCase(),
            },
            params: {
              skip: fetchDataOptions.pageIndex * fetchDataOptions.pageSize,
              take: fetchDataOptions.pageSize,
              searchProductionSite,
            },
          }
        )
      return res.data
    },

    placeholderData: keepPreviousData,
  })
}

export const useListSuppliersRiskQuery = ({
  fetchDataOptions,
  searchSupplier,
  filters,
}: riskQueryValidationType & fetchDataOptionsParam) => {
  return useQuery<LibraryResponses.SupplierRiskResponse>({
    queryKey: ["suppliers", fetchDataOptions, searchSupplier, filters],

    queryFn: async () => {
      const skip = fetchDataOptions.pageIndex * fetchDataOptions.pageSize
      const take = fetchDataOptions.pageSize
      const res = await httpClient.get<LibraryResponses.SupplierRiskResponse>(
        "suppliers/risk",
        {
          params: {
            skip,
            take,
            searchSupplier,
            filters: filters ? JSON.stringify(filters) : {},
          },
        }
      )
      return res.data
    },

    placeholderData: keepPreviousData,
  })
}
export const useTransferSupplierContactMutation = (
  options?: UseMutationOptions<
    LibraryResponses.Count,
    AxiosError,
    transferContactValidationType
  >
) => {
  return useMutation({
    ...options,
    mutationFn: async (req) => {
      return (await httpClient.post("suppliers/transfercontact", req)).data
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({ queryKey: ["suppliers"] })
      await queryClient.invalidateQueries({ queryKey: ["suppliersList"] })
      await queryClient.invalidateQueries({ queryKey: ["contactsList"] })
      await queryClient.invalidateQueries({ queryKey: ["listProductionSites"] })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export const useTransferSupplierContactCertificatesMutation = (
  options?: UseMutationOptions<
    LibraryResponses.Count,
    AxiosError,
    transferContactValidationType
  >
) => {
  const { t } = useTranslation("traceability")
  return useMutation({
    ...options,
    mutationFn: async (req) => {
      return (
        await httpClient.post("suppliers/transfercontact/certificates", req)
      ).data
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({ queryKey: ["suppliers"] })
      await queryClient.invalidateQueries({ queryKey: ["suppliersList"] })
      await queryClient.invalidateQueries({ queryKey: ["contactsList"] })
      await queryClient.invalidateQueries({ queryKey: ["listProductionSites"] })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      options?.onError?.(error, variables, onMutateResult, context)
      showAxiosError({
        notificationTitle: t("errors.title"),
        notificationMessage: t("errors.axiosMessage", {
          message: error.response?.data,
        }),
        showNotification: true,
        error,
      })
    },
  })
}
